import {dollarsPrettifier} from '~/utils/dollarsPrettifier'
import {
  type PercentageFee,
  type PerTransactionFee,
  type UsageBasedFee,
  type BankFeeProps,
  type USDWireFee,
} from './types'
import {
  dollarsAbbreviator,
  type DollarsAbbreviatorOptions,
} from '~/utils/dollarsAbbreviator'

const toCents = (n: number) => Math.round(n * 100)
const fromCents = (c: number) => c / 100

export const getTransactionOverageCost = ({limit, feeOverLimit}: UsageBasedFee) => {
  let costCents = 0
  if (feeOverLimit.numberOfTransactions > limit) {
    const excess = feeOverLimit.numberOfTransactions - limit
    costCents += excess * toCents(feeOverLimit.fee)
  }
  return fromCents(costCents)
}

export const getPerTransactionCost = (fee: PerTransactionFee) => {
  return fromCents(toCents(fee.fee) * fee.numberOfTransactions)
}

export const getPercentageFeeCost = (fee: PercentageFee) => {
  return fromCents(Math.round(fee.percentageFee * fee.amount))
}

export const getTotalUSDWiresCost = (
  domesticUSDWireFee: USDWireFee,
  internationalUSDWireFee: USDWireFee
) => {
  return (
    getPerTransactionCost(domesticUSDWireFee.outgoing) +
    getPerTransactionCost(domesticUSDWireFee.incoming) +
    getPerTransactionCost(internationalUSDWireFee.outgoing) +
    getPerTransactionCost(internationalUSDWireFee.incoming)
  )
}

export const getTotalMonthlyFees = ({
  maintenanceFee,
  transactionFee,
  domesticUSDWireFee,
  internationalUSDWireFee,
  foreignCurrencyInternationalWireFee,
}: BankFeeProps) => {
  const overage = getTransactionOverageCost(transactionFee)
  const USDWiresCost = getTotalUSDWiresCost(
    domesticUSDWireFee,
    internationalUSDWireFee
  )
  const fx = getPercentageFeeCost(foreignCurrencyInternationalWireFee)

  return maintenanceFee + overage + USDWiresCost + fx
}

export const abbreviateAmount = (
  value: number,
  options: Partial<DollarsAbbreviatorOptions> & {
    centsThreshold?: number
    threshold: number
  }
) => {
  const approximationChar =
    value > options.threshold ||
    (options.centsThreshold && value > options.centsThreshold)
      ? '~'
      : ''
  let amount = dollarsPrettifier(
    value,
    true,
    options.centsThreshold && value < options.centsThreshold ? 'twoWhenNecessary' : 0
  )

  if (value < options.threshold) return approximationChar + amount

  amount = dollarsAbbreviator(value, {
    neverShowCents: true,
    withDollarSign: true,
    withDecimals: 1,
    ...options,
  })

  if (value >= 1_000_000_000_000_000) {
    return '💰💰💰'
  }

  return approximationChar + amount
}
